import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

const EQUIPMENT = [
  { code: 'FLATBED', label: 'Flatbed' },
  { code: 'STEP_DECK', label: 'Step Deck' },
  { code: 'LOWBOY', label: 'Lowboy' },
  { code: 'DRY_VAN', label: 'Dry Van' },
  { code: 'REEFER', label: 'Reefer (Refrigerated)' },
  { code: 'TANKER', label: 'Tanker' },
  { code: 'HOTSHOT', label: 'Hotshot' },
  { code: 'POWER_ONLY', label: 'Power Only' },
];

const TX_CITIES = [
  'Houston', 'Dallas', 'San Antonio', 'Austin', 'Fort Worth',
  'Midland', 'Odessa', 'El Paso', 'Corpus Christi', 'Lubbock',
  'Amarillo', 'Beaumont', 'Permian Basin', 'Spring',
];

const fieldStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 10,
  border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.15s',
};

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#374151'
};

export default function ShipperRequestShipment() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { shipperEmail } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    originCity: '', originState: 'TX',
    destCity: '', destState: 'TX',
    commodity: '', weightLbs: '', pieces: '',
    equipment: 'FLATBED',
    pickupDate: '', deliveryDate: '',
    hazmat: false, hazmatClass: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.originCity || !form.destCity) {
      setError('Origin and destination cities are required');
      return;
    }
    if (!form.pickupDate) {
      setError('Pickup date is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/shipper/request-shipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-shipper-email': shipperEmail,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to submit');
      setSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!shipperEmail) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
      Please set a Shipper Email in the dev panel.
    </div>
  );

  if (success) return (
    <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
      <div style={{
        background: 'linear-gradient(135deg, #059669, #10b981)',
        borderRadius: 16, padding: 40, color: 'white', marginBottom: 24
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 900 }}>Shipment Request Submitted!</h2>
        <p style={{ margin: 0, fontSize: 15, opacity: 0.9 }}>{success.message}</p>
      </div>
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Load Number</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#111', marginTop: 4 }}>{success.loadNumber}</div>
        <p style={{ marginTop: 12, fontSize: 14, color: '#6b7280' }}>
          Your broker will review this request and provide a rate quote.
          You'll be notified when your shipment is ready.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button onClick={() => nav('/app/shipper/loads')}
          style={{ padding: '12px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
          📦 View My Loads
        </button>
        <button onClick={() => { setSuccess(null); setForm({ ...form, originCity: '', destCity: '', commodity: '', weightLbs: '', pieces: '', notes: '' }); }}
          style={{ padding: '12px 24px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
          ➕ Submit Another
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
        borderRadius: 14, padding: '24px 28px', marginBottom: 24, color: 'white'
      }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>➕ Request a Shipment</h1>
        <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.7 }}>
          Submit your freight details and your broker will provide a competitive quote.
        </p>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 16, fontWeight: 600 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Origin & Destination */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800 }}>📍 Route</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#059669', marginBottom: 12 }}>Origin</div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>City *</label>
                <input list="cities-origin" value={form.originCity} onChange={e => update('originCity', e.target.value)}
                  placeholder="e.g. Houston" style={fieldStyle} required />
                <datalist id="cities-origin">{TX_CITIES.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label style={labelStyle}>State *</label>
                <input value={form.originState} onChange={e => update('originState', e.target.value)}
                  placeholder="TX" maxLength={2} style={{ ...fieldStyle, width: 80, textTransform: 'uppercase' }} required />
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#dc2626', marginBottom: 12 }}>Destination</div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>City *</label>
                <input list="cities-dest" value={form.destCity} onChange={e => update('destCity', e.target.value)}
                  placeholder="e.g. Midland" style={fieldStyle} required />
                <datalist id="cities-dest">{TX_CITIES.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label style={labelStyle}>State *</label>
                <input value={form.destState} onChange={e => update('destState', e.target.value)}
                  placeholder="TX" maxLength={2} style={{ ...fieldStyle, width: 80, textTransform: 'uppercase' }} required />
              </div>
            </div>
          </div>
        </div>

        {/* Freight Details */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800 }}>📦 Freight Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Commodity / Description</label>
              <input value={form.commodity} onChange={e => update('commodity', e.target.value)}
                placeholder="e.g. Drilling equipment, pipe, etc."
                style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Equipment Type *</label>
              <select value={form.equipment} onChange={e => update('equipment', e.target.value)} style={fieldStyle}>
                {EQUIPMENT.map(eq => <option key={eq.code} value={eq.code}>{eq.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Weight (lbs)</label>
              <input type="number" value={form.weightLbs} onChange={e => update('weightLbs', e.target.value)}
                placeholder="e.g. 42000" style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Pieces</label>
              <input type="number" value={form.pieces} onChange={e => update('pieces', e.target.value)}
                placeholder="e.g. 1" style={fieldStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
            <input type="checkbox" id="hazmat" checked={form.hazmat}
              onChange={e => update('hazmat', e.target.checked)}
              style={{ width: 18, height: 18 }} />
            <label htmlFor="hazmat" style={{ fontSize: 14, fontWeight: 600 }}>⚠️ Hazardous Materials</label>
            {form.hazmat && (
              <input value={form.hazmatClass} onChange={e => update('hazmatClass', e.target.value)}
                placeholder="Hazmat class (e.g. 3, 8)"
                style={{ ...fieldStyle, width: 200, marginLeft: 12 }} />
            )}
          </div>
        </div>

        {/* Schedule */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800 }}>📅 Schedule</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Requested Pickup Date *</label>
              <input type="date" value={form.pickupDate} onChange={e => update('pickupDate', e.target.value)}
                style={fieldStyle} required />
            </div>
            <div>
              <label style={labelStyle}>Requested Delivery Date</label>
              <input type="date" value={form.deliveryDate} onChange={e => update('deliveryDate', e.target.value)}
                style={fieldStyle} />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800 }}>📝 Additional Notes</h3>
          <textarea value={form.notes} onChange={e => update('notes', e.target.value)}
            placeholder="Special instructions, access requirements, wellsite directions, etc."
            rows={3} style={{ ...fieldStyle, resize: 'vertical' }} />
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" disabled={submitting}
            style={{
              padding: '14px 32px', background: submitting ? '#9ca3af' : '#2563eb',
              color: 'white', border: 'none', borderRadius: 12, fontWeight: 800,
              fontSize: 16, cursor: submitting ? 'default' : 'pointer',
              transition: 'all 0.15s'
            }}>
            {submitting ? '⏳ Submitting...' : '🚛 Submit Shipment Request'}
          </button>
          <button type="button" onClick={() => nav('/app/shipper')}
            style={{ padding: '14px 24px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
